import { supabase } from '../lib/supabase';
import { puterCoachingService } from './puterCoachingService';
import lumoPersonalityService from './lumoPersonalityService';
import puterAI from '../lib/puter';

/**
 * Micro-Lesson Service
 * Delivers 2-3 minute targeted lessons based on recent mistakes and growth areas
 */

/**
 * Identify learning opportunities from recent performance
 */
export const identifyLearningOpportunities = async (userId) => {
  try {
    // Get last 10 drill attempts
    const { data: recentDrills, error: drillError } = await supabase
      .from('user_drills')
      .select(`
        *,
        drills (
          id,
          prompt_text,
          difficulty_level,
          category_id,
          drill_categories (
            name
          )
        )
      `)
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })
      .limit(10);

    if (drillError) throw drillError;

    if (!recentDrills || recentDrills.length === 0) {
      return [];
    }

    // Analyze score patterns
    const opportunities = [];

    // Find consistently low-scoring categories
    const categoryScores = {};
    recentDrills.forEach(drill => {
      const categoryId = drill.drills?.category_id || 'general';
      const categoryName = drill.drills?.drill_categories?.name || categoryId;
      
      if (!categoryScores[categoryId]) {
        categoryScores[categoryId] = { 
          name: categoryName,
          total: 0, 
          count: 0, 
          scores: [] 
        };
      }
      const avgScore = (
        (drill.clarity_score || 0) +
        (drill.confidence_score || 0) +
        (drill.control_score || 0) +
        (drill.close_score || 0)
      ) / 4;
      categoryScores[categoryId].total += avgScore;
      categoryScores[categoryId].count += 1;
      categoryScores[categoryId].scores.push(avgScore);
    });

    // Identify weak categories (avg < 70)
    Object.entries(categoryScores).forEach(([categoryId, stats]) => {
      const avg = stats.total / stats.count;
      if (avg < 70) {
        opportunities.push({
          type: 'weak_category',
          category: stats.name, // Use category name instead of ID
          avgScore: Math.round(avg),
          priority: 100 - avg, // Lower score = higher priority
          recentScores: stats.scores.slice(0, 3)
        });
      }
    });

    // Find specific skill gaps (clarity, confidence, control, close)
    const skillScores = {
      clarity: [],
      confidence: [],
      control: [],
      close: []
    };

    recentDrills.forEach(drill => {
      if (drill.clarity_score) skillScores.clarity.push(drill.clarity_score);
      if (drill.confidence_score) skillScores.confidence.push(drill.confidence_score);
      if (drill.control_score) skillScores.control.push(drill.control_score);
      if (drill.close_score) skillScores.close.push(drill.close_score);
    });

    Object.entries(skillScores).forEach(([skill, scores]) => {
      if (scores.length > 0) {
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        if (avg < 70) {
          opportunities.push({
            type: 'weak_skill',
            skill,
            avgScore: Math.round(avg),
            priority: 100 - avg,
            recentScores: scores.slice(0, 3)
          });
        }
      }
    });

    // Find declining performance (last 3 vs previous 3)
    if (recentDrills.length >= 6) {
      const recent3Avg = recentDrills.slice(0, 3).reduce((sum, drill) => {
        return sum + ((drill.clarity_score + drill.confidence_score + drill.control_score + drill.close_score) / 4);
      }, 0) / 3;

      const previous3Avg = recentDrills.slice(3, 6).reduce((sum, drill) => {
        return sum + ((drill.clarity_score + drill.confidence_score + drill.control_score + drill.close_score) / 4);
      }, 0) / 3;

      if (recent3Avg < previous3Avg - 10) { // 10+ point drop
        opportunities.push({
          type: 'performance_decline',
          recentAvg: Math.round(recent3Avg),
          previousAvg: Math.round(previous3Avg),
          decline: Math.round(previous3Avg - recent3Avg),
          priority: Math.round(previous3Avg - recent3Avg)
        });
      }
    }

    // Sort by priority (highest first)
    return opportunities.sort((a, b) => b.priority - a.priority);
  } catch (error) {
    console.error('Failed to identify learning opportunities:', error);
    return [];
  }
};

/**
 * Generate a micro-lesson based on identified opportunity
 */
export const generateMicroLesson = async (userId, profile, opportunity) => {
  try {
    // Get personality-adjusted system prompt
    const systemPrompt = lumoPersonalityService.getSystemPrompt(profile, 'microlesson');

    let lessonPrompt = '';

    if (opportunity.type === 'weak_category') {
      lessonPrompt = `The user has been struggling with ${opportunity.category} drills, averaging ${opportunity.avgScore}/100 on their last ${opportunity.recentScores.length} attempts.

Create a 2-3 minute micro-lesson to help them improve in this category:
1. Identify the key challenge in ${opportunity.category}
2. Explain 1-2 proven tactics/frameworks
3. Provide a concrete example
4. Give an actionable next step

Keep it concise, practical, and match the personality tone from the system prompt.`;
    } else if (opportunity.type === 'weak_skill') {
      const skillDescriptions = {
        clarity: 'clear, concise communication without filler words or rambling',
        confidence: 'strong, assured delivery with conviction',
        control: 'managing the conversation flow and objections smoothly',
        close: 'asking for commitment and advancing the sale'
      };

      lessonPrompt = `The user is averaging ${opportunity.avgScore}/100 on the "${opportunity.skill}" skill (${skillDescriptions[opportunity.skill]}).

Create a 2-3 minute micro-lesson:
1. Explain why ${opportunity.skill} matters in sales
2. Share 1-2 tactics to improve ${opportunity.skill}
3. Provide a specific example or script
4. Give a drill/practice exercise they can do today

Keep it actionable and match the personality tone.`;
    } else if (opportunity.type === 'performance_decline') {
      lessonPrompt = `The user's performance has dropped from ${opportunity.previousAvg}/100 to ${opportunity.recentAvg}/100 (${opportunity.decline}-point decline) over their last 6 drills.

Create a 2-3 minute micro-lesson to help them recover:
1. Acknowledge the slump (it happens to everyone)
2. Identify potential causes (fatigue, rushing, loss of focus)
3. Share tactics to get back on track
4. Provide motivation and a concrete next step

Keep it supportive but direct, matching the personality tone.`;
    }

    // Generate lesson using Puter AI
    const response = await puterAI.generateContent(
      systemPrompt + '\n\n' + lessonPrompt,
      { temperature: 0.8, model: 'claude-sonnet-4-5' }
    );
    
    // Parse Puter AI response - handle different response formats
    let lessonContent = 'Lesson generation failed';
    if (typeof response === 'string') {
      lessonContent = response;
    } else if (response?.content?.[0]?.text) {
      lessonContent = response.content[0].text;
    } else if (response?.message) {
      lessonContent = response.message;
    } else if (response?.content) {
      lessonContent = response.content;
    }

    // Save lesson to database
    const { data, error } = await supabase
      .from('micro_lessons')
      .insert({
        user_id: userId,
        industry: 'general', // Default industry for AI-generated lessons
        title: `${opportunity.type} improvement`,
        lesson_text: lessonContent,
        key_steps: lessonPrompt,
        opportunity_type: opportunity.type,
        opportunity_data: opportunity,
        lesson_content: lessonContent,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      content: lessonContent,
      opportunity,
      createdAt: data.created_at
    };
  } catch (error) {
    console.error('Failed to generate micro-lesson:', error);
    throw error;
  }
};

/**
 * Get suggested micro-lesson for user (if available)
 */
export const getSuggestedMicroLesson = async (userId, profile) => {
  try {
    // Check if user has recent lesson (within last 24 hours)
    // Only consider lessons created after the fix was deployed (Dec 10, 2025)
    const fixDeployedAt = new Date('2025-12-10T00:00:00Z');
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const cutoffDate = fixDeployedAt > oneDayAgo ? fixDeployedAt : oneDayAgo;
    
    const { data: recentLesson, error: recentError } = await supabase
      .from('micro_lessons')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', cutoffDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Handle table not found (404) and no rows found (PGRST116) gracefully
    if (recentError) {
      // If table doesn't exist or user doesn't have permission, return null instead of throwing
      if (recentError.code === '404' || recentError.status === 404 || recentError.code === 'PGRST116') {
        return null;
      }
      throw recentError;
    }

    // If recent lesson exists and not completed, return it
    if (recentLesson && !recentLesson.completed_at) {
      // Parse content - handle both lesson_text and lesson_content fields
      let content = recentLesson.lesson_text || recentLesson.lesson_content || '';
      
      // If content is JSON string, try to parse it
      if (content.startsWith('{') || content.startsWith('[')) {
        try {
          const parsed = JSON.parse(content);
          if (parsed.content?.[0]?.text) {
            content = parsed.content[0].text;
          }
        } catch (e) {
          // Not valid JSON, use as-is
        }
      }
      
      return {
        id: recentLesson.id,
        content: content,
        opportunity: recentLesson.opportunity_data,
        createdAt: recentLesson.created_at,
        isNew: false
      };
    }

    // Identify opportunities
    const opportunities = await identifyLearningOpportunities(userId);

    if (opportunities.length === 0) {
      return null; // No opportunities = user is doing great!
    }

    // Generate new lesson for top opportunity
    const topOpportunity = opportunities[0];
    const newLesson = await generateMicroLesson(userId, profile, topOpportunity);

    return {
      ...newLesson,
      isNew: true
    };
  } catch (error) {
    console.error('Failed to get suggested micro-lesson:', error);
    return null;
  }
};

/**
 * Mark micro-lesson as completed
 */
export const completeMicroLesson = async (lessonId, userId) => {
  try {
    const { error } = await supabase
      .from('micro_lessons')
      .update({
        completed_at: new Date().toISOString()
      })
      .eq('id', lessonId)
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to complete micro-lesson:', error);
    return false;
  }
};

/**
 * Get user's micro-lesson history
 */
export const getMicroLessonHistory = async (userId, limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('micro_lessons')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch micro-lesson history:', error);
    return [];
  }
};

/**
 * Get micro-lesson completion stats
 */
export const getMicroLessonStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('micro_lessons')
      .select('id, completed_at')
      .eq('user_id', userId);

    if (error) throw error;

    const total = data?.length || 0;
    const completed = data?.filter(l => l.completed_at).length || 0;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      total,
      completed,
      pending: total - completed,
      completionRate
    };
  } catch (error) {
    console.error('Failed to fetch micro-lesson stats:', error);
    return { total: 0, completed: 0, pending: 0, completionRate: 0 };
  }
};

export default {
  identifyLearningOpportunities,
  generateMicroLesson,
  getSuggestedMicroLesson,
  completeMicroLesson,
  getMicroLessonHistory,
  getMicroLessonStats
};
